'use client';

import {
  Badge,
  Field,
  HttpEditor,
  IconButton,
  Input,
  Text,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { CreateAiApplicationDto } from '@terra/shared-contracts-ai-applications';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  readInterfaceBodyExample,
  withInterfaceBodyExample,
  type AiApplicationInterfaceMetadata,
} from './aiInterfaceMetadata';
import {
  coerceInterfaceHttpRequest,
  canScaffoldInterfaceHttpRequest,
  resolveUrlFromInterfaceHttpRequest,
  supportsInterfaceHttpRequest,
} from './aiInterfaceHttpRequest';
import {
  AiApplicationProtocolSelector,
  type AiApplicationInterfaceType,
} from './AiApplicationProtocolSelector';

export type AiApplicationInterfaceRow = {
  id?: string;
  name?: string | null;
  url: string;
  type: AiApplicationInterfaceType;
  bodyExample?: string | null;
  probeStatus?: 'PENDING' | 'VERIFIED' | 'FAILED' | 'SKIPPED' | null;
  discoveryFailureReason?: string | null;
};
type AiApplicationInterfacesEditorProps = {
  baseUrl: string;
  onBaseUrlChange: (value: string) => void;
  selectedProtocols?: AiApplicationInterfaceType[];
  onSelectedProtocolsChange?: (protocols: AiApplicationInterfaceType[]) => void;
  interfaceRows?: AiApplicationInterfaceRow[];
  onInterfaceRowsChange?: (rows: AiApplicationInterfaceRow[]) => void;
  baseUrlError?: string;
  isBaseUrlDisabled?: boolean;
  showProbeStatus?: boolean;
  showProtocolSelector?: boolean;
  urlPlaceholder?: string;
};

function getProbeStatusPalette(
  probeStatus: AiApplicationInterfaceRow['probeStatus'],
) {
  switch (probeStatus) {
    case 'VERIFIED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'SKIPPED':
      return 'gray';
    case 'PENDING':
      return 'yellow';
    default:
      return 'gray';
  }
}

export function syncInterfaceRows({
  baseUrl,
  selectedProtocols,
  interfaceRows,
}: {
  baseUrl: string;
  selectedProtocols: AiApplicationInterfaceType[];
  interfaceRows: AiApplicationInterfaceRow[];
}): AiApplicationInterfaceRow[] {
  const protocols = selectedProtocols.filter(
    (protocol) => protocol !== 'VOICE',
  );

  return protocols.map((type) => {
    const existing = interfaceRows.find((row) => row.type === type);
    const rowUrl = existing?.url ?? baseUrl;

    return {
      id: existing?.id,
      name: existing?.name ?? type,
      url: rowUrl,
      type,
      bodyExample:
        existing?.bodyExample ??
        (supportsInterfaceHttpRequest(type) &&
        canScaffoldInterfaceHttpRequest(rowUrl)
          ? coerceInterfaceHttpRequest({
              stored: existing?.bodyExample,
              url: rowUrl,
            })
          : null),
      probeStatus: existing?.probeStatus ?? null,
      discoveryFailureReason: existing?.discoveryFailureReason ?? null,
    };
  });
}

export function AiApplicationInterfacesEditor({
  baseUrl,
  onBaseUrlChange,
  selectedProtocols = [],
  onSelectedProtocolsChange,
  interfaceRows = [],
  onInterfaceRowsChange,
  baseUrlError,
  isBaseUrlDisabled = false,
  showProbeStatus = false,
  showProtocolSelector = true,
  urlPlaceholder,
}: AiApplicationInterfacesEditorProps) {
  const t = useTranslations('aiApplications');
  const handleProtocolsChange = (protocols: AiApplicationInterfaceType[]) => {
    onSelectedProtocolsChange?.(protocols);
    onInterfaceRowsChange?.(
      syncInterfaceRows({
        baseUrl,
        selectedProtocols: protocols,
        interfaceRows,
      }),
    );
  };

  const handleBaseUrlChange = (value: string) => {
    onBaseUrlChange(value);
    onInterfaceRowsChange?.(
      interfaceRows.map((row) =>
        row.url === baseUrl ? { ...row, url: value } : row,
      ),
    );
  };

  const handleRemoveInterface = (type: AiApplicationInterfaceType) => {
    if (selectedProtocols.length <= 1) {
      return;
    }

    handleProtocolsChange(
      selectedProtocols.filter((protocol) => protocol !== type),
    );
  };

  const getProtocolLabel = (type: AiApplicationInterfaceRow['type']) =>
    t(`interfaceTypeOptions.${type}`);

  return (
    <VStack alignItems="stretch" gap={6}>
      <Field.Root required invalid={Boolean(baseUrlError)}>
        <Field.Label>{t('fields.url')}</Field.Label>
        <Input
          value={baseUrl}
          onChange={(event) => handleBaseUrlChange(event.target.value)}
          placeholder={urlPlaceholder ?? t('fields.urlPlaceholder')}
          disabled={isBaseUrlDisabled}
        />
        {baseUrlError ? (
          <Field.ErrorText>{baseUrlError}</Field.ErrorText>
        ) : null}
      </Field.Root>

      {showProtocolSelector ? (
        <VStack alignItems="stretch" gap={3}>
          <Text fontSize="sm" fontWeight="medium">
            {t('fields.interfaceTypes')}
          </Text>
          <AiApplicationProtocolSelector
            selectedProtocols={selectedProtocols}
            onSelectedProtocolsChange={handleProtocolsChange}
          />
          <Text fontSize="xs" color="fg.muted">
            {t('wizard.interfaces.protocolHint')}
          </Text>
        </VStack>
      ) : null}

      {showProtocolSelector && interfaceRows.length > 0 ? (
        <VStack alignItems="stretch" gap={4}>
          {interfaceRows.map((row, index) => (
            <VStack
              key={`${row.type}-${row.id ?? index}`}
              alignItems="stretch"
              gap={3}
              borderWidth="1px"
              borderColor="border.neutral.muted"
              borderRadius="md"
              p={4}
            >
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="medium">
                  {getProtocolLabel(row.type)}
                </Text>
                <HStack gap={2} alignItems="center">
                  {showProbeStatus && row.probeStatus ? (
                    <Badge
                      variant="outline"
                      size="sm"
                      colorPalette={getProbeStatusPalette(row.probeStatus)}
                    >
                      {t(`settings.general.probeStatus.${row.probeStatus}`)}
                    </Badge>
                  ) : null}
                  {selectedProtocols.length > 1 ? (
                    <IconButton
                      aria-label={t('wizard.interfaces.remove', {
                        index: getProtocolLabel(row.type),
                      })}
                      variant="ghost"
                      size="xs"
                      color="fg.muted"
                      onClick={() => handleRemoveInterface(row.type)}
                    >
                      <Cancel01Icon size={14} />
                    </IconButton>
                  ) : null}
                </HStack>
              </HStack>

              <Field.Root>
                <Field.Label>{t('fields.interfaceName')}</Field.Label>
                <Input
                  value={row.name ?? ''}
                  onChange={(event) => {
                    const nextRows = interfaceRows.map((item, itemIndex) =>
                      itemIndex === index
                        ? { ...item, name: event.target.value || null }
                        : item,
                    );

                    onInterfaceRowsChange?.(nextRows);
                  }}
                />
              </Field.Root>

              {supportsInterfaceHttpRequest(row.type) ? (
                <Field.Root>
                  <Field.Label>{t('fields.httpRequest')}</Field.Label>
                  <Box
                    borderWidth="1px"
                    borderColor="border.neutral.muted"
                    borderRadius="md"
                    overflow="hidden"
                    minH="180px"
                  >
                    <HttpEditor
                      value={coerceInterfaceHttpRequest({
                        stored: row.bodyExample,
                        url: row.url,
                      })}
                      onChange={(nextValue) => {
                        const trimmed = nextValue.trim();
                        const nextUrl = resolveUrlFromInterfaceHttpRequest({
                          rawRequest: trimmed,
                          fallbackUrl: row.url,
                        });
                        const nextRows = interfaceRows.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                url: nextUrl,
                                bodyExample: trimmed || null,
                              }
                            : item,
                        );

                        onInterfaceRowsChange?.(nextRows);
                      }}
                      minHeight="180px"
                      maxHeight="280px"
                      showViewModeToggle
                      wordWrap
                    />
                  </Box>
                  <Field.HelperText>
                    {t('fields.httpRequestHint')}
                  </Field.HelperText>
                </Field.Root>
              ) : (
                <Field.Root>
                  <Field.Label>{t('fields.url')}</Field.Label>
                  <Input
                    value={row.url}
                    onChange={(event) => {
                      const nextRows = interfaceRows.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, url: event.target.value }
                          : item,
                      );

                      onInterfaceRowsChange?.(nextRows);
                    }}
                  />
                </Field.Root>
              )}

              {showProbeStatus && row.discoveryFailureReason ? (
                <Text fontSize="xs" color="fg.error">
                  {row.discoveryFailureReason}
                </Text>
              ) : null}
            </VStack>
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}

export function buildCreateInterfacesFromRows(
  rows: AiApplicationInterfaceRow[],
): CreateAiApplicationDto['interfaces'] {
  return rows.map((row) => ({
    id: row.id,
    name: row.name ?? null,
    url: row.url,
    type: row.type,
    authSummary: null,
    metadata: withInterfaceBodyExample({
      metadata: { discoveryPending: true, probeStatus: 'PENDING' as const },
      bodyExample: row.bodyExample,
    }),
  }));
}

export function mapStoredInterfaceToRow({
  id,
  name,
  url,
  type,
  metadata,
  probeStatus,
  discoveryFailureReason,
}: {
  id: string;
  name?: string | null;
  url: string;
  type: AiApplicationInterfaceType;
  metadata: AiApplicationInterfaceMetadata;
  probeStatus?: AiApplicationInterfaceRow['probeStatus'];
  discoveryFailureReason?: string | null;
}): AiApplicationInterfaceRow {
  return {
    id,
    name,
    url,
    type,
    bodyExample: supportsInterfaceHttpRequest(type)
      ? coerceInterfaceHttpRequest({
          stored: readInterfaceBodyExample(metadata),
          url,
        })
      : readInterfaceBodyExample(metadata),
    probeStatus: probeStatus ?? null,
    discoveryFailureReason: discoveryFailureReason ?? null,
  };
}
